import { PARALLEL_BLOCKS, blockListUrl, blockListXml, blockUrl, planBlocks } from './blockUpload'

/**
 * Puts a file straight into Azure Blob storage through a write SAS for that one blob, as blocks, then commits
 * them. Bytes never pass through the Functions API, whose 100 MB request limit is why a multi-gigabyte recording
 * cannot take the asset upload path. Needs blob CORS on the storage account for the dashboard's origin.
 */

export interface UploadFileToBlobOptions {
    /** Stored as the blob's Content-Type when the blocks are committed. */
    contentType: string
    signal: AbortSignal
    /** Bytes in blocks that have landed so far. */
    onProgress: (uploadedBytes: number) => void
    /** Swapped in tests; the page uses the global fetch. */
    fetchImpl?: typeof fetch
    /** Swapped in tests so retries take no real time. */
    wait?: (milliseconds: number, signal: AbortSignal) => Promise<void>
}

/** Storage answered, and not with success: retried only when the answer says a retry can help. */
export class BlobUploadError extends Error {
    constructor(message: string, readonly status: number | null) {
        super(message)
        this.name = 'BlobUploadError'
    }
}

const ATTEMPTS_PER_REQUEST = 3
const RETRY_DELAY_MS = 1000

function isRetryable(status: number): boolean {
    return status === 408 || status === 429 || status >= 500
}

function waitFor(milliseconds: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal.aborted) { reject(signal.reason); return }
        const timer = setTimeout(() => { signal.removeEventListener('abort', onAbort); resolve() }, milliseconds)
        const onAbort = () => { clearTimeout(timer); reject(signal.reason) }
        signal.addEventListener('abort', onAbort, { once: true })
    })
}

async function putWithRetry(
    url: string, body: BodyInit, headers: Record<string, string>, signal: AbortSignal,
    fetchImpl: typeof fetch, wait: NonNullable<UploadFileToBlobOptions['wait']>,
): Promise<void> {
    for (let attempt = 1; ; attempt++) {
        let failure: BlobUploadError
        try {
            const response = await fetchImpl(url, { method: 'PUT', body, headers, signal })
            if (response.ok) return
            failure = new BlobUploadError(`Storage refused part of the upload (HTTP ${response.status}).`, response.status)
            if (!isRetryable(response.status)) throw failure
        } catch (error) {
            if (signal.aborted || error instanceof BlobUploadError) throw error
            failure = new BlobUploadError('The connection dropped during the upload.', null)
        }
        if (attempt >= ATTEMPTS_PER_REQUEST) throw failure
        await wait(RETRY_DELAY_MS * attempt, signal)
    }
}

/**
 * Uploads `file` to `uploadUrl` (a SAS URL with create and write rights), PARALLEL_BLOCKS blocks at a time, and
 * commits them in order. Rejects with the AbortSignal's reason when cancelled, or a BlobUploadError; the first
 * failing block stops the others.
 */
export async function uploadFileToBlob(file: Blob, uploadUrl: string, options: UploadFileToBlobOptions): Promise<void> {
    const fetchImpl = options.fetchImpl ?? fetch
    const wait = options.wait ?? waitFor
    const blocks = planBlocks(file.size)
    const stop = new AbortController()
    const onOuterAbort = () => stop.abort(options.signal.reason)
    if (options.signal.aborted) throw options.signal.reason
    options.signal.addEventListener('abort', onOuterAbort, { once: true })

    let next = 0
    let uploadedBytes = 0
    const worker = async () => {
        while (next < blocks.length && !stop.signal.aborted) {
            const block = blocks[next++]
            await putWithRetry(blockUrl(uploadUrl, block.id), file.slice(block.start, block.end), {}, stop.signal, fetchImpl, wait)
            uploadedBytes += block.end - block.start
            options.onProgress(uploadedBytes)
        }
    }

    try {
        const workers = Array.from({ length: Math.min(PARALLEL_BLOCKS, blocks.length) }, () => worker().catch(error => {
            stop.abort(error)
            throw error
        }))
        const results = await Promise.allSettled(workers)
        const rejected = results.find((result): result is PromiseRejectedResult => result.status === 'rejected')
        if (options.signal.aborted) throw options.signal.reason
        if (rejected) throw rejected.reason
        await putWithRetry(
            blockListUrl(uploadUrl),
            blockListXml(blocks.map(block => block.id)),
            { 'Content-Type': 'application/xml', 'x-ms-blob-content-type': options.contentType },
            stop.signal, fetchImpl, wait,
        )
    } finally {
        options.signal.removeEventListener('abort', onOuterAbort)
    }
}
